import React, { useState } from 'react';
import {
  Box,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Chip,
  Menu,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Alert,
  Snackbar,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  MoreVert,
  Refresh,
  Visibility,
  Cancel,
  EventBusy,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { SubscriptionDetailsModal } from './SubscriptionDetailsModal';

interface Subscription {
  id: string;
  customer: {
    id: string;
    email: string;
    name: string;
  } | null;
  plan: {
    id: string;
    product: string;
    nickname: string | null;
    amount: number;
    currency: string;
    interval: string;
  } | null;
  status: string;
  current_period_start: number;
  current_period_end: number;
  cancel_at_period_end: boolean;
  canceled_at: number | null;
  created: number;
}

interface CancelDialogState {
  open: boolean;
  subscription: Subscription | null;
  cancelImmediately: boolean;
}

export const SubscriptionsTable: React.FC = () => {
  const { authenticatedFetch } = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  const [statusFilter, setStatusFilter] = useState<string>('');
  const [excludeInternal, setExcludeInternal] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [detailsSubscription, setDetailsSubscription] = useState<Subscription | null>(null);
  const [cancelDialog, setCancelDialog] = useState<CancelDialogState>({
    open: false,
    subscription: null,
    cancelImmediately: false,
  });
  const [cancelNote, setCancelNote] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Fetch subscriptions
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['stripe-subscriptions', statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      params.append('limit', '100');

      const response = await authenticatedFetch(
        `/api/marketcompass/admin/stripe/subscriptions?${params.toString()}`
      );
      if (!response || !response.ok) {
        throw new Error('Failed to fetch subscriptions');
      }
      return response.json();
    },
  });

  // Cancel subscription mutation
  const cancelMutation = useMutation({
    mutationFn: async ({ subscriptionId, cancelImmediately, note }: { subscriptionId: string; cancelImmediately: boolean; note: string }) => {
      const response = await authenticatedFetch(
        `/api/marketcompass/admin/stripe/subscriptions/${subscriptionId}/cancel`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ cancelImmediately, note }),
        }
      );
      if (!response || !response.ok) {
        const error = response ? await response.json() : null;
        throw new Error(error?.error || 'Failed to cancel subscription');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stripe-subscriptions'] });
      setSnackbar({ open: true, message: 'Subscription cancelled successfully', severity: 'success' });
      setCancelDialog({ open: false, subscription: null, cancelImmediately: false });
      setCancelNote('');
    },
    onError: (error: Error) => {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    },
  });

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, subscription: Subscription) => {
    event.stopPropagation(); // Prevent row click from firing
    setAnchorEl(event.currentTarget);
    setSelectedSubscription(subscription);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedSubscription(null);
  };

  const handleCancelClick = (cancelImmediately: boolean) => {
    if (selectedSubscription) {
      setCancelDialog({
        open: true,
        subscription: selectedSubscription,
        cancelImmediately,
      });
    }
    handleMenuClose();
  };

  const handleCancelConfirm = () => {
    if (cancelDialog.subscription) {
      cancelMutation.mutate({
        subscriptionId: cancelDialog.subscription.id,
        cancelImmediately: cancelDialog.cancelImmediately,
        note: cancelNote,
      });
    }
  };

  const handleCancelDialogClose = () => {
    setCancelDialog({ open: false, subscription: null, cancelImmediately: false });
    setCancelNote('');
  };

  const handleRowClick = (subscription: Subscription) => {
    setDetailsSubscription(subscription);
    setDetailsModalOpen(true);
  };

  const handleDetailsModalClose = () => {
    setDetailsModalOpen(false);
    setDetailsSubscription(null);
  };

  // Format date
  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Format currency
  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  // Get status color
  const getStatusColor = (status: string): 'success' | 'default' | 'error' | 'primary' | 'warning' => {
    switch (status) {
      case 'active':
        return 'success';
      case 'canceled':
        return 'default';
      case 'past_due':
      case 'incomplete_expired':
        return 'error';
      case 'trialing':
        return 'primary';
      case 'incomplete':
        return 'warning';
      default:
        return 'default';
    }
  };

  // Filter subscriptions based on excludeInternal checkbox
  const filteredSubscriptions = (data?.data?.subscriptions || []).filter(
    (subscription: Subscription) =>
      !excludeInternal || !subscription.customer?.email?.toLowerCase().includes('younge')
  );

  const columns: GridColDef[] = [
    {
      field: 'customer',
      headerName: 'Customer',
      flex: 1,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams<Subscription>) => (
        <Box>
          <Box>{params.row.customer?.email || 'N/A'}</Box>
          {params.row.customer?.name && (
            <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              {params.row.customer.name}
            </Box>
          )}
        </Box>
      ),
    },
    {
      field: 'plan',
      headerName: 'Plan',
      flex: 1,
      minWidth: 180,
      renderCell: (params: GridRenderCellParams<Subscription>) => (
        <Box>
          <Box>{params.row.plan?.nickname || 'N/A'}</Box>
          {params.row.plan && (
            <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              {params.row.plan.interval}
            </Box>
          )}
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (params: GridRenderCellParams<Subscription>) => (
        <Chip
          label={params.row.status}
          color={getStatusColor(params.row.status)}
          size="small"
        />
      ),
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 120,
      renderCell: (params: GridRenderCellParams<Subscription>) => (
        params.row.plan ? formatCurrency(params.row.plan.amount, params.row.plan.currency) : 'N/A'
      ),
    },
    {
      field: 'current_period',
      headerName: 'Current Period',
      flex: 1,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams<Subscription>) => (
        <Box>
          {formatDate(params.row.current_period_start)} - {formatDate(params.row.current_period_end)}
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 80,
      sortable: false,
      renderCell: (params: GridRenderCellParams<Subscription>) => (
        <IconButton
          size="small"
          onClick={(e) => handleMenuClick(e, params.row)}
        >
          <MoreVert />
        </IconButton>
      ),
    },
  ];

  return (
    <Box>
      {/* Filters */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          select
          label="Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          size="small"
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="canceled">Canceled</MenuItem>
          <MenuItem value="past_due">Past Due</MenuItem>
          <MenuItem value="trialing">Trialing</MenuItem>
          <MenuItem value="incomplete">Incomplete</MenuItem>
          <MenuItem value="incomplete_expired">Incomplete Expired</MenuItem>
        </TextField>

        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={() => refetch()}
        >
          Refresh
        </Button>

        <FormControlLabel
          control={
            <Checkbox
              checked={excludeInternal}
              onChange={(e) => setExcludeInternal(e.target.checked)}
              size="small"
            />
          }
          label="Exclude internal subscriptions"
        />
      </Box>

      {/* Table */}
      <Box sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={filteredSubscriptions}
          columns={columns}
          loading={isLoading}
          pageSizeOptions={[25, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
          }}
          onRowClick={(params) => handleRowClick(params.row)}
          sx={{
            '& .MuiDataGrid-row': {
              cursor: 'pointer',
            },
          }}
        />
      </Box>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleCancelClick(true)} disabled={selectedSubscription?.status === 'canceled'}>
          <ListItemIcon>
            <Cancel fontSize="small" />
          </ListItemIcon>
          <ListItemText>Cancel Immediately</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleCancelClick(false)} disabled={selectedSubscription?.status === 'canceled'}>
          <ListItemIcon>
            <EventBusy fontSize="small" />
          </ListItemIcon>
          <ListItemText>Cancel at Period End</ListItemText>
        </MenuItem>
      </Menu>

      {/* Cancel Confirmation Dialog */}
      <Dialog
        open={cancelDialog.open}
        onClose={handleCancelDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {cancelDialog.cancelImmediately ? 'Cancel Subscription Immediately?' : 'Cancel Subscription at Period End?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {cancelDialog.cancelImmediately ? (
              <>
                This will cancel the subscription immediately and the customer will lose access.
                This action cannot be undone.
              </>
            ) : (
              <>
                The subscription will remain active until the end of the current billing period.
                The customer will retain access until {cancelDialog.subscription && formatDate(cancelDialog.subscription.current_period_end)}.
              </>
            )}
          </DialogContentText>

          {cancelDialog.subscription && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Box><strong>Customer:</strong> {cancelDialog.subscription.customer?.email}</Box>
              <Box><strong>Plan:</strong> {cancelDialog.subscription.plan?.nickname}</Box>
            </Box>
          )}

          <TextField
            label="Internal Note (optional)"
            multiline
            rows={3}
            fullWidth
            value={cancelNote}
            onChange={(e) => setCancelNote(e.target.value)}
            sx={{ mt: 2 }}
            placeholder="Add an internal note about why this subscription is being cancelled..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDialogClose}>Cancel</Button>
          <Button
            onClick={handleCancelConfirm}
            color="error"
            variant="contained"
            disabled={cancelMutation.isPending}
            startIcon={cancelMutation.isPending ? <CircularProgress size={16} /> : null}
          >
            Confirm Cancellation
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Subscription Details Modal */}
      <SubscriptionDetailsModal
        open={detailsModalOpen}
        onClose={handleDetailsModalClose}
        subscription={detailsSubscription}
      />
    </Box>
  );
};
